import { ReactNode } from "react";
import { useSupplierEntitlements } from "@/hooks/useSupplierEntitlements";
import { Lock, Crown } from "lucide-react";

interface FeatureGateProps {
  featureKey: string;
  supplierId: string | null | undefined;
  children: ReactNode;
  lockedFallback?: ReactNode;
  supplierProfile?: {
    enabled_services?: string[];
    enabled_plugins?: string[];
    premium_modules?: string[];
  };
}

function DefaultLockedFallback({ label }: { label: string }) {
  return (
    <div className="relative rounded-lg border border-dashed border-gray-300 bg-gray-50/50 p-6 text-center">
      <div className="flex flex-col items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
          <Lock className="h-5 w-5 text-amber-600" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-700">{label}</p>
          <p className="mt-1 text-xs text-gray-500">
            Tato funkce je dostupná v Premium plánu
          </p>
        </div>
        <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-200">
          <Crown className="h-3 w-3" />
          Premium
        </div>
      </div>
    </div>
  );
}

export function FeatureGate({
  featureKey,
  supplierId,
  children,
  lockedFallback,
  supplierProfile,
}: FeatureGateProps) {
  const { getFeature, isLoading } = useSupplierEntitlements(supplierId, supplierProfile);

  // While loading, render nothing to prevent flash
  if (isLoading) return null;

  const feature = getFeature(featureKey);

  // If feature not found in catalog, allow access (fail-open for undefined features)
  if (!feature) return <>{children}</>;

  if (feature.status === "allowed") {
    return <>{children}</>;
  }

  if (feature.status === "hidden") {
    return null;
  }

  if (feature.status === "read_only") {
    return (
      <div className="pointer-events-none select-none opacity-60">
        {children}
      </div>
    );
  }

  // status === "locked"
  return <>{lockedFallback || <DefaultLockedFallback label={feature.label} />}</>;
}
